import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useNotification } from '../hooks/useNotification';
import { debtorService } from '../services/debtorService';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import {
  FiDollarSign,
  FiShoppingCart,
  FiAlertTriangle,
  FiClock,
  FiDownload,
  FiBell,
  FiTrendingUp
} from 'react-icons/fi';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { daysRemaining, showWarning } = useSubscription();
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load actual debtors for Top 10
      const debtorsResponse = await debtorService.getAll({ limit: 100, sortBy: 'totalOutstanding', sortOrder: 'desc' });
      const allDebtors = debtorsResponse.data.debtors || [];

      // Get top 10 debtors by outstanding amount
      const top10Debtors = allDebtors
        .filter(d => d.totalOutstanding > 0)
        .slice(0, 10)
        .map(d => ({
          id: d._id,
          name: d.name,
          count: 0, // Would need transaction count from backend
          amount: d.totalOutstanding
        }));

      // Mock data for other sections (to be replaced with actual API calls)
      const mockData = {
        kpis: {
          todayLending: 56.850,
          last7DaysLending: 506.850,
          currentMonth: 856.380,
          pending31to45Days: 562.740,
          highRiskAlert: 74.965
        },
        todaySummary: {
          customersLentTo: 9,
          totalTransactions: 68,
          itemsLent: 256
        },
        weeklyStats: [
          { day: 'Today', amount: 56.85, count: 9, lenders: 3 },
          { day: '15-Oct', amount: 78.30, count: 12, lenders: 4 },
          { day: '14-Oct', amount: 92.15, count: 15, lenders: 5 },
          { day: '13-Oct', amount: 65.40, count: 10, lenders: 3 },
          { day: '12-Oct', amount: 88.75, count: 14, lenders: 4 },
          { day: '11-Oct', amount: 71.20, count: 11, lenders: 3 },
          { day: '10-Oct', amount: 54.30, count: 8, lenders: 2 }
        ],
        top10Debtors: top10Debtors.length > 0 ? top10Debtors : [
          { name: 'No debtors yet', count: 0, amount: 0 }
        ],
        latestLending: [
          {
            datetime: '28-Oct-2025 15:46',
            customerName: 'Recent Transaction',
            amount: 0,
            items: 'No recent transactions'
          }
        ]
      };

      setDashboardData(mockData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleKPIClick = (metric) => {
    // Navigate to filtered lending history
    navigate('/lending', { state: { filter: metric } });
  };

  const handleCustomerClick = (customerName) => {
    // Navigate to customer detail page
    navigate('/customers', { state: { search: customerName } });
  };

  // Export handlers
  const handleExportTodayLending = () => {
    try {
      const csvData = [
        ['Metric', 'Value'],
        ['Customers Lent To', dashboardData.todaySummary.customersLentTo],
        ['Total Transactions', dashboardData.todaySummary.totalTransactions],
        ['Items Lent', dashboardData.todaySummary.itemsLent]
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `today_lending_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showSuccess('Today\'s lending data exported successfully!');
    } catch (error) {
      showError('Failed to export data');
    }
  };

  const handleExportWeeklyStats = () => {
    try {
      const csvData = [
        ['Day', 'Amount (BHD)', 'Count', 'Lenders'],
        ...dashboardData.weeklyStats.map(stat => [
          stat.day,
          stat.amount.toFixed(3),
          stat.count,
          stat.lenders
        ])
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly_lending_stats_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showSuccess('Weekly statistics exported successfully!');
    } catch (error) {
      showError('Failed to export data');
    }
  };

  const handleExportTop10Debtors = () => {
    try {
      const csvData = [
        ['Rank', 'Name', 'Count', 'Amount (BHD)'],
        ...dashboardData.top10Debtors.map((debtor, index) => [
          index + 1,
          debtor.name,
          debtor.count,
          debtor.amount.toFixed(3)
        ])
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `top10_debtors_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showSuccess('Top 10 debtors exported successfully!');
    } catch (error) {
      showError('Failed to export data');
    }
  };

  const handleExportLatestLending = () => {
    try {
      const csvData = [
        ['Date & Time', 'Customer Name', 'Amount (BHD)', 'Items'],
        ...dashboardData.latestLending.map(transaction => [
          transaction.datetime,
          transaction.customerName,
          transaction.amount.toFixed(3),
          transaction.items
        ])
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `latest_lending_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showSuccess('Latest lending data exported successfully!');
    } catch (error) {
      showError('Failed to export data');
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!dashboardData) return null;

  return (
    <Layout title="Dashboard">
      {/* Expiry Warning Banner */}
      {showWarning && (
        <div className="bg-warning-100 border-l-4 border-warning-500 p-4 rounded-card mb-6">
          <p className="text-warning-800 flex items-center">
            <FiBell className="mr-2" />
            Your subscription expires in {daysRemaining} days. Please renew to avoid interruption.
          </p>
        </div>
      )}

      {/* KPI Cards - 5 Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div
          onClick={() => handleKPIClick('today')}
          className="cursor-pointer hover:scale-105 transition-transform"
        >
          <StatCard
            icon={FiDollarSign}
            value={`BHD ${dashboardData.kpis.todayLending.toFixed(3)}`}
            label="Total Lending Today"
            bgColor="bg-pastel-pink"
            iconColor="text-white"
            iconBgColor="bg-danger-500"
          />
        </div>

        <div
          onClick={() => handleKPIClick('last7days')}
          className="cursor-pointer hover:scale-105 transition-transform"
        >
          <StatCard
            icon={FiShoppingCart}
            value={`BHD ${dashboardData.kpis.last7DaysLending.toFixed(3)}`}
            label="Last 7 Days Lending"
            bgColor="bg-pastel-mint"
            iconColor="text-white"
            iconBgColor="bg-success-500"
          />
        </div>

        <div
          onClick={() => handleKPIClick('currentMonth')}
          className="cursor-pointer hover:scale-105 transition-transform"
        >
          <StatCard
            icon={FiTrendingUp}
            value={`BHD ${dashboardData.kpis.currentMonth.toFixed(3)}`}
            label="Current Month"
            bgColor="bg-pastel-lavender"
            iconColor="text-white"
            iconBgColor="bg-primary-500"
          />
        </div>

        <div
          onClick={() => handleKPIClick('pending31to45')}
          className="cursor-pointer hover:scale-105 transition-transform"
        >
          <StatCard
            icon={FiClock}
            value={`BHD ${dashboardData.kpis.pending31to45Days.toFixed(3)}`}
            label="Pending (31-45 Days)"
            bgColor="bg-pastel-peach"
            iconColor="text-white"
            iconBgColor="bg-warning-500"
          />
        </div>

        <div
          onClick={() => handleKPIClick('highRisk')}
          className="cursor-pointer hover:scale-105 transition-transform"
        >
          <StatCard
            icon={FiAlertTriangle}
            value={`BHD ${dashboardData.kpis.highRiskAlert.toFixed(3)}`}
            label="High Risk Alert"
            bgColor="bg-red-50"
            iconColor="text-white"
            iconBgColor="bg-danger-600"
          />
        </div>
      </div>

      {/* Today's Lending Summary */}
      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-900">Today's Lending</h2>
          <Button variant="outline" size="sm" onClick={handleExportTodayLending}>
            <FiDownload className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-primary-600">
              {dashboardData.todaySummary.customersLentTo}
            </div>
            <div className="text-sm text-neutral-600 mt-1">Customers Lent To</div>
          </div>
          <div className="bg-success-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-success-600">
              {dashboardData.todaySummary.totalTransactions}
            </div>
            <div className="text-sm text-neutral-600 mt-1">Total Transactions</div>
          </div>
          <div className="bg-warning-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-warning-600">
              {dashboardData.todaySummary.itemsLent}
            </div>
            <div className="text-sm text-neutral-600 mt-1">Items Lent</div>
          </div>
        </div>
      </div>

      {/* Two Column Layout for Chart and Top 10 */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        {/* Current Week - Lending Statistics Chart */}
        <div className="lg:col-span-3 bg-white rounded-card shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-neutral-900">Current Week - Lending Statistics</h2>
            <Button variant="outline" size="sm" onClick={handleExportWeeklyStats}>
              <FiDownload className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Vertical Grouped Bar Chart */}
          <div className="relative pt-4 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <style>{`
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {/* Chart Area with Grid Lines */}
            <div className="relative h-80 border-l-2 border-b-2 border-neutral-300 min-w-[600px]">
              {/* Horizontal Grid Lines */}
              <div className="absolute inset-0">
                {[0, 20, 40, 60, 80, 100, 120].map((value) => (
                  <div
                    key={value}
                    className="absolute w-full border-t border-neutral-200"
                    style={{ bottom: `${(value / 120) * 100}%` }}
                  >
                    <span className="absolute -left-8 -top-2 text-xs text-neutral-500">
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Bars Container */}
              <div className="absolute inset-0 flex items-end justify-around gap-4 px-2">
                {dashboardData.weeklyStats.map((stat, index) => {
                  const maxValue = 120; // Fixed max value for consistent scale

                  return (
                    <div key={index} className="flex flex-col items-center flex-1">
                      {/* Grouped Bars */}
                      <div className="flex items-end gap-0.5 mb-2">
                        {/* Amount Bar */}
                        <div className="flex flex-col items-center">
                          <div className="relative group">
                            {/* Value Label on top */}
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-primary-100 px-2 py-0.5 rounded text-xs font-semibold text-primary-700 whitespace-nowrap">
                              {stat.amount.toFixed(3)}
                            </div>
                            {/* Bar */}
                            <div
                              className="w-10 bg-primary-600 rounded-t transition-all duration-500 hover:bg-primary-700"
                              style={{ height: `${(stat.amount / maxValue) * 300}px` }}
                            ></div>
                          </div>
                        </div>

                        {/* Count Bar */}
                        <div className="flex flex-col items-center">
                          <div className="relative group">
                            {/* Value Label on top */}
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-neutral-200 px-2 py-0.5 rounded text-xs font-semibold text-neutral-700">
                              {stat.count}
                            </div>
                            {/* Bar */}
                            <div
                              className="w-10 bg-neutral-400 rounded-t transition-all duration-500 hover:bg-neutral-500"
                              style={{ height: `${(stat.count / maxValue) * 300}px` }}
                            ></div>
                          </div>
                        </div>

                        {/* Lenders Bar */}
                        <div className="flex flex-col items-center">
                          <div className="relative group">
                            {/* Value Label on top */}
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-orange-100 px-2 py-0.5 rounded text-xs font-semibold text-orange-700">
                              {stat.lenders}
                            </div>
                            {/* Bar */}
                            <div
                              className="w-10 bg-orange-300 rounded-t transition-all duration-500 hover:bg-orange-400"
                              style={{ height: `${(stat.lenders / maxValue) * 300}px` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Day Label */}
                      <div className="text-xs font-medium text-neutral-700 mt-2">
                        {stat.day}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-6 mt-6 text-sm text-neutral-700 justify-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-primary-600 rounded"></div>
              <span>Amount</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-neutral-400 rounded"></div>
              <span>Count</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-300 rounded"></div>
              <span>Lenders</span>
            </div>
          </div>
        </div>

        {/* Current Week - Top 10 Debtors */}
        <div className="lg:col-span-2 bg-white rounded-card shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-neutral-900">Current Week - Top 10 Debtors</h2>
            <Button variant="outline" size="sm" onClick={handleExportTop10Debtors}>
              <FiDownload className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">#</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Name</th>
                  <th className="text-right py-3 px-2 text-sm font-semibold text-neutral-700">Count</th>
                  <th className="text-right py-3 px-2 text-sm font-semibold text-neutral-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.top10Debtors.map((debtor, index) => (
                  <tr
                    key={index}
                    onClick={() => handleCustomerClick(debtor.name)}
                    className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-2 text-sm text-neutral-600">{index + 1}</td>
                    <td className="py-3 px-2">
                      <div className="font-medium text-neutral-900">{debtor.name}</div>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="inline-flex items-center justify-center bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium">
                        {debtor.count}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-semibold text-danger-600">
                        BHD {debtor.amount.toFixed(3)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Latest Lending - Quick View */}
      <div className="bg-white rounded-card shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-900">Latest Lending - Quick View</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/lending')}>
              View All
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportLatestLending}>
              <FiDownload className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Date & Time</th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Customer Name</th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-neutral-700">Amount</th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-neutral-700">Items (Brief)</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.latestLending.map((transaction, index) => (
                <tr
                  key={index}
                  onClick={() => handleCustomerClick(transaction.customerName)}
                  className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors"
                >
                  <td className="py-3 px-2 text-sm text-neutral-600">
                    {transaction.datetime}
                  </td>
                  <td className="py-3 px-2">
                    <div className="font-medium text-neutral-900">{transaction.customerName}</div>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="font-semibold text-success-600">
                      BHD {transaction.amount.toFixed(3)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-sm text-neutral-600">
                    {transaction.items}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer with Version */}
      <div className="mt-6 text-center text-sm text-neutral-500">
        BBBooks v1.0.5
      </div>
    </Layout>
  );
};

export default DashboardPage;
